import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Square task completion button — always visible, always left-side.
 * size:           'sm' (16px, list/card rows) | 'md' (20px, detail panel)
 * pendingApproval: task is in an approval section
 * onApprove:       if provided, user has approval rights → shows orange check they can click
 *                  if absent, user can only wait → shows orange clock
 */
export function TaskCheckbox({ completed, onClick, pendingApproval = false, onApprove, size = 'sm', className }) {
  const dim = size === 'md' ? 'w-5 h-5' : 'w-4 h-4'
  const clockSize = size === 'md' ? 11 : 9

  const isApprovable = pendingApproval && !completed && onApprove
  const isWaiting    = pendingApproval && !completed && !onApprove

  function handleClick(e) {
    if (isWaiting) return
    if (isApprovable) onApprove(e)
    else onClick?.(e)
  }

  return (
    <button
      onClick={handleClick}
      aria-checked={completed}
      aria-disabled={isWaiting || undefined}
      aria-label={isApprovable ? 'Approve task' : isWaiting ? 'Pending approval' : completed ? 'Mark incomplete' : 'Mark complete'}
      title={isApprovable ? 'Approve task' : isWaiting ? 'Pending approval' : undefined}
      className={cn(
        dim,
        'relative flex-shrink-0 inline-flex items-center justify-center rounded border',
        completed
          ? 'bg-[#34d399] border-[#34d399]'
          : isApprovable
            ? 'bg-[#ff9500]/10 border-[#ff9500]/80 hover:bg-[#ff9500]/20 hover:border-[#ff9500]'
            : isWaiting
              ? 'bg-[#ff9500]/8 border-[#ff9500]/50 cursor-default'
              : 'border-text-faint/60 hover:border-text-faint hover:bg-surface-raised',
        className,
      )}
      style={{ transition: 'background 120ms ease, border-color 120ms ease, transform 100ms ease-out' }}
      onMouseDown={(e) => { if (!isWaiting) e.currentTarget.style.transform = 'scale(0.82)' }}
      onMouseUp={(e)   => { e.currentTarget.style.transform = 'scale(1)' }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
    >
      {isWaiting ? (
        /* Clock: you can't approve, just waiting */
        <Clock size={clockSize} strokeWidth={2.5} style={{ color: '#ff9500', opacity: 0.7 }} />
      ) : isApprovable ? (
        /* Orange checkmark: click to approve */
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="shrink-0 pointer-events-none">
          <path
            d="M14 7L8.5 12.5L6 10"
            stroke="#ff9500"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        /* Green checkmark: completed */
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="shrink-0 pointer-events-none">
          <path
            d="M14 7L8.5 12.5L6 10"
            stroke="white"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ opacity: completed ? 1 : 0, transition: 'opacity 100ms ease' }}
          />
        </svg>
      )}
    </button>
  )
}
